import { useEffect, useRef, useState } from "react";
import { FolderPlus, Loader2, Plus } from "lucide-react";
import { api, type Collection } from "../lib/api";

export function useCollections(dep = 0) {
  const [list, setList] = useState<Collection[] | null>(null);
  const reload = () => api<Collection[]>("collections").then(setList).catch(() => setList([]));
  useEffect(() => { reload(); }, [dep]);
  return { list, reload };
}

/**
 * Lägg markerade bilder i en samling – befintlig eller ny. Används i markeringslisten och på bildsidan.
 * `dark` = knappen sitter i den mörka markeringslisten.
 */
export function AddToCollection({ ids, onDone, dark, label = "Lägg i samling" }: {
  ids: string[]; onDone: (msg: string) => void; dark?: boolean; label?: string;
}) {
  const [open, setOpen] = useState(false);
  const { list, reload } = useCollections();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", esc); };
  }, []);
  const add = async (c: { id: string; name: string }) => {
    setBusy(true);
    try { await api(`collections/${c.id}/assets`, { method: "POST", json: { ids } }); onDone(`Lagt till i ”${c.name}”.`); setOpen(false); }
    finally { setBusy(false); }
  };
  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api<{ id: string }>("collections", { method: "POST", json: { name, ids } });
      onDone(`Skapade ”${name.trim()}” med ${ids.length} bild${ids.length === 1 ? "" : "er"}.`);
      setName(""); setOpen(false); reload();
    } finally { setBusy(false); }
  };
  return (
    <div ref={ref} className="relative">
      <button onClick={() => { setOpen(!open); if (!open) reload(); }}
        className={dark ? "flex items-center gap-1.5 rounded-lg bg-white/10 px-2.5 py-1 hover:bg-white/20" : "btn"}>
        <FolderPlus size={14} /> {label}
      </button>
      {open && (
        <div className={`absolute z-50 w-72 rounded-xl border border-line bg-card p-2 text-ink shadow-2xl ${dark ? "bottom-full left-1/2 mb-2 -translate-x-1/2" : "left-0 top-full mt-1"}`}>
          <div className="max-h-60 overflow-y-auto">
            {list === null && <Loader2 size={14} className="m-2 animate-spin text-muted" />}
            {list?.length === 0 && <p className="p-2 text-[13px] text-muted">Inga samlingar ännu.</p>}
            {list?.map((c) => (
              <button key={c.id} disabled={busy} onClick={() => add(c)}
                className="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left hover:bg-mist">
                <span className="truncate">{c.name}</span><span className="text-[12px] text-muted">{c.count}</span>
              </button>
            ))}
          </div>
          <form className="mt-1 flex gap-1 border-t border-line pt-2" onSubmit={(e) => { e.preventDefault(); create(); }}>
            <input className="field !py-1" placeholder="Ny samling…" value={name} onChange={(e) => setName(e.target.value)} />
            <button className="btn-primary !px-2" disabled={busy || !name.trim()} aria-label="Skapa samling"><Plus size={15} /></button>
          </form>
        </div>
      )}
    </div>
  );
}

